import {Cell, Column, Row, Table, ValueType, Workbook, Worksheet} from "documonster/excel";
import type {INumericFieldSettingsDefinition} from "../models/field/datatypes/Numeric";
import type {View} from "../models/View";
import type {IModel} from "../models/Model";
import {FileUtils} from "../../utils/file/FileUtils";
import {StringUtils} from "../../utils/data/string/StringUtils";
import {MathUtils} from "../../utils/math/MathUtils";
import {ImageUtils} from "../../utils/image/ImageUtils";
import {DateFormatter} from "../../utils/format/DateFormatter";
import type {XyiconFeature} from "../../generated/api/base";
import {FieldDataType} from "../../generated/api/base";
import {ObjectUtils} from "../../utils/data/ObjectUtils";
import type {Report} from "../models/Report";
import {REPORT_COUNT_FIELD} from "../state/AppFields";
import {BaseExporter} from "./BaseExporter";

interface IAdditionalInfo {
	"View Name": string;
	"Generated Date": string;
	"Exported By": string;
}

export class ExcelExporter extends BaseExporter {
	protected readonly _extension = "xlsx";
	// https://docs.microsoft.com/en-us/office/troubleshoot/excel/determine-column-widths
	private readonly _defaultFontSize: number = 11;
	private readonly _defaultFontFamily: string = "Calibri";

	// https://docs.microsoft.com/en-us/office/troubleshoot/excel/determine-column-widths
	private readonly _defaultColumnWidth: number = 8.43;
	private readonly _maxColumnWidth: number = 40;

	// excel text size in excelcolumnsize / measured text with canvas 3d context in px -> concrete numbers are derived with measuring an example
	private readonly _excelToCtxRatio: number = 6.43 / 31.077;

	private addAdditionalInfo(workSheet: Worksheet.Handle, additionalInfo: IAdditionalInfo) {
		for (const key in additionalInfo) {
			Worksheet.addRow(workSheet, [key, additionalInfo[key as keyof typeof additionalInfo]]);
		}
	}

	private formatColumns(workSheet: Worksheet.Handle, fieldRefIds: string[]) {
		for (let i = 0; i < fieldRefIds.length; ++i) {
			const fieldRefId = fieldRefIds[i];
			const field = this._appState.actions.getFieldByRefId(fieldRefId);

			if (field && fieldRefId !== REPORT_COUNT_FIELD) {
				if (field.dataType === FieldDataType.Numeric && field.dataTypeSettings) {
					const dataTypeSettings = field.dataTypeSettings as INumericFieldSettingsDefinition;
					const decimals = MathUtils.isValidNumber(dataTypeSettings?.decimals) ? dataTypeSettings.decimals : 0;

					let numFmt = `0${decimals === 0 ? "" : "."}${new Array(decimals + 1).join("0")}`;

					if (dataTypeSettings.formatting === "percentage") {
						numFmt = `${numFmt}%`;
					}

					if (dataTypeSettings.formatting === "comma-style" || dataTypeSettings.formatting === "currency") {
						numFmt = `#,##${numFmt}`;

						if (dataTypeSettings.formatting === "currency") {
							numFmt = `$${numFmt}`;
						}
					}
					Column.setNumFmt(workSheet, i + 1, numFmt);
				}
			} else if (fieldRefId === REPORT_COUNT_FIELD) {
				let numFmt = "0";

				Column.setNumFmt(workSheet, i + 1, numFmt);
			}
		}
	}

	private addData<T extends IModel>(workSheet: Worksheet.Handle, view: View, items: T[]) {
		const fieldRefIds = this.getFieldRefIds(view).filter((f) => !f.includes("icon"));

		const headers = this.getHeaders(fieldRefIds);

		Worksheet.addRow(workSheet, headers);

		this.formatColumns(workSheet, fieldRefIds);

		for (const item of items) {
			const row: (string | number)[] = [];

			for (let i = 0; i < fieldRefIds.length; ++i) {
				const fieldRefId = fieldRefIds[i];
				const field = this._appState.actions.getFieldByRefId(fieldRefId);

				const fieldValues = this.getFieldData(item, fieldRefId).filter(StringUtils.isTruthy).flat();

				if (field.dataType === FieldDataType.Numeric && fieldValues.length === 1) {
					const fieldValueAsFloat = Number(fieldValues[0]);

					row.push(isNaN(fieldValueAsFloat) ? fieldValues[0] : fieldValueAsFloat);
				} else {
					row.push(fieldValues.join("\n"));
				}
			}

			Worksheet.addRow(workSheet, row);
		}

		for (let index = 0; index < Worksheet.columnCount(workSheet); ++index) {
			const field = this._appState.actions.getFieldByRefId(fieldRefIds[index]);
			const {formatting, decimals} = !field.default && field.dataTypeSettings;

			if (field.dataType === FieldDataType.Numeric && formatting === "percentage") {
				Column.setNumFmt(workSheet, index + 1, `0${decimals > 0 ? "." : ""}${"0".repeat(decimals)}%`);
			}
		}
	}

	// ExcelJS's eachCell (which this code was written for) skipped the empty cells, so we do the same
	private hasValue(workSheet: Worksheet.Handle, rowNumber: number, colNumber: number) {
		return Cell.find(workSheet, rowNumber, colNumber) !== undefined && Cell.getType(workSheet, rowNumber, colNumber) !== ValueType.Null;
	}

	private formatSheet(workSheet: Worksheet.Handle, filterButton: boolean) {
		const filterButtonMargin = filterButton ? "ww" : "";
		const columnCount = Worksheet.columnCount(workSheet);

		Worksheet.eachRow(workSheet, (row, rowNumber: number) => {
			let maxNewLineCount = 0;

			for (let colNumber = 1; colNumber <= columnCount; ++colNumber) {
				if (!this.hasValue(workSheet, rowNumber, colNumber)) {
					continue;
				}

				const newLineCount = Cell.getText(workSheet, rowNumber, colNumber).match(/\n/g)?.length || 0;

				maxNewLineCount = Math.max(maxNewLineCount, newLineCount);

				if (newLineCount > 0) {
					Cell.setAlignment(workSheet, rowNumber, colNumber, {wrapText: true});
				}
			}

			if (maxNewLineCount > 0) {
				let height = Row.getHeight(workSheet, rowNumber);

				if (!MathUtils.isValidNumber(height)) {
					height = workSheet.properties.defaultRowHeight;
				}
				Row.setHeight(workSheet, rowNumber, height * (maxNewLineCount + 1));
			}
		});

		for (let colNumber = 1; colNumber <= columnCount; ++colNumber) {
			let maxCellWidth = this._defaultColumnWidth;

			Worksheet.eachRow(workSheet, (row, rowNumber: number) => {
				if (!this.hasValue(workSheet, rowNumber, colNumber)) {
					return;
				}

				const textToMeasure = `${Cell.getText(workSheet, rowNumber, colNumber)}${filterButtonMargin} `;
				const fontWeight = Cell.getFont(workSheet, rowNumber, colNumber)?.bold ? "bold" : "normal";

				maxCellWidth = Math.max(
					maxCellWidth,
					ImageUtils.getTextWidthInPx(textToMeasure, this._defaultFontFamily, this._defaultFontSize, fontWeight) * this._excelToCtxRatio,
				);
			});

			Column.setWidth(workSheet, colNumber, Math.min(maxCellWidth, this._maxColumnWidth));
		}
	}

	protected async _exportView<T extends IModel>(view: View, items: T[]) {
		const workBook = Workbook.create();
		const workSheet = Workbook.addWorksheet(workBook);

		this.addAdditionalInfo(workSheet, {
			"View Name": view.name,
			"Generated Date": DateFormatter.format(new Date().toString()),
			"Exported By": this._appState.user.fullName,
		});

		// 2 empty lines
		Worksheet.addRows(workSheet, [[], []]);

		this.addData(workSheet, view, items);

		this.formatSheet(workSheet, false);

		const fileName = this.getFileName(view);
		const buffer = (await Workbook.toBuffer(workBook)) as BlobPart;

		FileUtils.downloadFileGivenByData(buffer, fileName, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
	}

	public async exportTable(data: (string | number)[][], fileName: string, feature: XyiconFeature, report?: Report) {
		let clonedData = ObjectUtils.deepClone(data) || [];

		const headers = clonedData[0]?.map((v) => `${v}`) || [];

		clonedData = clonedData.slice(1);

		// https://stackoverflow.com/questions/68103638/exceljs-we-found-a-problem-with-some-content-in-filename-xlsx
		// eslint-disable-next-line sonarjs/single-character-alternation
		const fileNameWithoutForbiddenChars = fileName.replace(/\*|\?|\:|\\|\/|\[|\]|\<|\>|\||\"| /g, "_"); // cannot include any of the following characters: * ? : \ / [ ] < > | "

		// Also: `Worksheet name <worksheetname> exceeds 31 chars. This will be truncated`
		const workSheetName = fileNameWithoutForbiddenChars.substring(0, 31);

		const workBook = Workbook.create();
		const workSheet = Workbook.addWorksheet(workBook, workSheetName);

		const fieldRefIds = report
			? [...report.displayedFields.map((f) => f.field), ...report.displayedLinkedFields.map((f) => f.field)]
			: headers.map((h) => this._appState.actions.getFieldByName(feature, h)).map((f) => f?.refId);

		this.formatColumns(workSheet, fieldRefIds);

		for (let j = 0; j < clonedData.length; ++j) {
			for (let i = 0; i < fieldRefIds.length; ++i) {
				const field = this._appState.actions.getFieldByRefId(fieldRefIds[i]);

				if (field) {
					if (field.dataType === FieldDataType.Numeric) {
						const cell = `${clonedData[j][i]}`;
						const values = cell.split("\n");

						if (values.length > 1) {
							clonedData[j][i] = values.map((v) => this._appState.actions.formatValue(v, field.refId) as string).join("\n");
						}
					}
				}
			}
		}

		const filterButton = true;

		Table.add(workSheet, {
			name: "Table",
			ref: "A1",
			columns: headers.map((h) => ({name: h, filterButton: filterButton})),
			rows: clonedData,
			style: {
				theme: "TableStyleMedium2",
				showRowStripes: true,
			},
		});

		this.formatSheet(workSheet, filterButton);
		const buffer = (await Workbook.toBuffer(workBook)) as BlobPart;

		FileUtils.downloadFileGivenByData(buffer, fileNameWithoutForbiddenChars, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
	}
}
